/** Screen switching. Rooms are full-screen and mount fresh, so each one owns
 *  exactly one canvas and one animation loop. */

import { useCallback, useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import type { Picture } from './lib/store.ts';
import { store } from './lib/store.ts';
import type { Scene } from './lib/scene.ts';
import { decodeScene } from './lib/scene.ts';
import { backupFilename, buildBackup, download } from './lib/backup.ts';
import { token } from './engine/palette.ts';
import { ambientWanted, startAmbient, stopAmbient } from './engine/ambient.ts';
import { Home } from './Home.tsx';
import type { Room } from './Home.tsx';
import { DoodlePad } from './modes/DoodlePad.tsx';
import { AliveLab } from './modes/AliveLab.tsx';
import { MathLab } from './modes/MathLab.tsx';
import { ShapeLab } from './modes/ShapeLab.tsx';
import { PatternLab } from './modes/PatternLab.tsx';
import { MusicLab } from './modes/MusicLab.tsx';
import { VoxelLab } from './modes/VoxelLab.tsx';
import { Toast } from './ui/controls.tsx';

interface Opening {
  room: Room;
  /** Set when a saved picture is being opened to draw on some more. */
  scene: Scene | null;
}

interface Notice {
  message: string;
  actionLabel?: string;
  action?: () => void;
}

export function App(): JSX.Element {
  const [open, setOpen] = useState<Opening | null>(null);
  const [pictures, setPictures] = useState<Picture[]>([]);
  const [busy, setBusy] = useState(true);
  const [notice, setNotice] = useState<Notice | null>(null);

  const refresh = useCallback(async () => {
    const db = await store();
    setPictures(await db.list());
    setBusy(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Music that was on last time comes back on. It stays silent until the first
  // tap, because every browser refuses to start audio before one.
  useEffect(() => {
    if (ambientWanted()) startAmbient();
    return () => stopAmbient();
  }, []);

  const goHome = useCallback(() => {
    setOpen(null);
    void refresh();
  }, [refresh]);

  // The hardware/browser back button leaves the room instead of the app.
  useEffect(() => {
    const onPop = () => setOpen(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const enter = (room: Room, scene: Scene | null = null) => {
    setOpen({ room, scene });
    window.history.pushState({ room }, '');
  };

  /** Putting a picture away is reversible, and says so the moment it happens. */
  const archive = async (id: string) => {
    const db = await store();
    await db.archive(id);
    await refresh();
    setNotice({
      message: 'Put away',
      actionLabel: 'Undo',
      action: () => {
        void (async () => {
          await (await store()).restore(id);
          await refresh();
          setNotice(null);
        })();
      },
    });
  };

  const restore = async (id: string) => {
    await (await store()).restore(id);
    await refresh();
  };

  const deleteForever = async (id: string) => {
    await (await store()).deleteForever(id);
    await refresh();
    setNotice({ message: 'Deleted' });
  };

  const exportAll = () => {
    setBusy(true);
    // Rendering every picture at full size blocks a frame or two; let the
    // button's disabled state paint before the work starts.
    setTimeout(() => {
      try {
        const now = new Date();
        download(buildBackup(pictures, token('--paper'), now), backupFilename(now));
        setNotice({ message: `Saved ${pictures.length} picture${pictures.length === 1 ? '' : 's'} to your computer` });
      } catch {
        setNotice({ message: 'That did not save. Try once more' });
      } finally {
        setBusy(false);
      }
    }, 30);
  };

  const reopen = (picture: Picture) => {
    const scene = picture.scene ? decodeScene(picture.scene) : null;
    if (!scene) {
      // Kept before the marks were stored — there is nothing to draw on.
      setNotice({ message: 'This one was saved as a picture only' });
      return;
    }
    enter(scene.kind === 'mirror' ? 'shapes' : 'doodle', scene);
  };

  if (open) {
    switch (open.room) {
      case 'doodle':
        return <DoodlePad onExit={goHome} onSaved={refresh} initial={open.scene} />;
      case 'alive':
        return <AliveLab onExit={goHome} />;
      case 'math':
        return <MathLab onExit={goHome} />;
      case 'patterns':
        return <PatternLab onExit={goHome} />;
      case 'shapes':
        return <ShapeLab onExit={goHome} onSaved={refresh} initial={open.scene} />;
      case 'build':
        return <VoxelLab onExit={goHome} />;
      case 'music':
        return <MusicLab onExit={goHome} />;
    }
  }

  return (
    <>
      <Home
        pictures={pictures}
        busy={busy}
        onOpen={enter}
        onReopen={reopen}
        onArchive={(id) => void archive(id)}
        onRestore={(id) => void restore(id)}
        onDeleteForever={(id) => void deleteForever(id)}
        onExport={exportAll}
      />
      {notice && (
        <Toast
          message={notice.message}
          actionLabel={notice.actionLabel}
          onAction={notice.action}
          onDone={() => setNotice(null)}
        />
      )}
    </>
  );
}
